
import {Component, Vue} from "vue-property-decorator";
import {ProgramPaths} from "@/enum/ProgramPaths";
import SayfaBilgiInfo from "@/components/SayfaBilgiInfo.vue";
import RaporOzetKarti from "@/components/rapor/RaporOzetKarti.vue";
import {RaporExcelKolon, raporExcelIndir} from "@/utils/raporExcelExport";
import {sunucuExcelKaydet, sunucuExcelYokla as sunucuExcelYoklaOrtak} from "@/utils/sunucuExcelYoklama";

@Component({
  components: {SayfaBilgiInfo, RaporOzetKarti},
})

export default class RaporDetayView extends Vue {
  headers: Array<object> = [];
  // DEV-3224 — Excel sütunları: başlık + kod + sayısal mı. Sayısal işareti
  // katalogdan gelir (kosul_input === 'tutar'); eskiden burada elle tutulan
  // kod listesi vardı ve her yeni parasal alanda güncellenmesi unutuluyordu.
  excelKolonlari: RaporExcelKolon[] = [];
  items: Array<object> = [];
  isReady: boolean = false;
  raporAdi: string = "";
  programPaths = ProgramPaths;
  printMode = false;

  grafikTercihi: string = "kartlar";
  sunucuExcelDurum: string = "";

  // DEV-3224 — ekrandaki satırları gerçek .xlsx olarak indirir. Eski yol bir
  // HTML tablosunu .xls adıyla veriyordu; Excel hem "dosya bozuk olabilir"
  // uyarısı veriyor hem de tipsiz hücreleri kendi yereline göre yorumlayıp
  // "10.75" gibi değerleri tarihe çeviriyordu (TDT-3484, TDT-3487).
  excelIndir() {
    raporExcelIndir({
      kolonlar: this.excelKolonlari,
      satirlar: this.excelSatirlari,
      raporAdi: this.raporAdi,
    });
  }

  // DEV-3097 — Excel'i sunucuda kuyruğa üretip indirir (istemci Excel'e alternatif)
  sunucudaExcel() {
    this.sunucuExcelDurum = "hazirlaniyor";
    this.$http.get("/api/v1/rapor/" + this.$route.params.id + "/export").then((response: any) => {
      this.sunucuExcelYokla(response.data.is_key);
    });
  }

  // DEV-3224 (TDT-3483) — yoklama mantığı ortak yardımcıda: "bulunamadi" hata
  // sayılmaz (iş kuyrukta sırasını bekliyor olabilir), 5 dakikalık tavan var ve
  // mesaj gerçekte ne olduğunu söyler. Aynı akış AI sihirbazında da kullanılıyor;
  // kopyalamak yerine tek yere taşındı.
  sunucuExcelYokla(isKey: string) {
    sunucuExcelYoklaOrtak({
      durumSor: () => this.$http.get("/api/v1/rapor-ai/excel-durum/" + isKey)
          .then((response: any) => response.data.durum),
      hazir: () => {
        this.sunucuExcelDurum = "";
        this.$http.get("/api/v1/rapor-ai/excel-indir/" + isKey, {responseType: "blob"})
            .then((govde: any) => {
              sunucuExcelKaydet(govde, this.raporAdi + ".xlsx", (mesaj: string) => this.$toast.error(mesaj));
            });
      },
      hata: (metin: string) => {
        this.sunucuExcelDurum = "";
        this.$toast.error(metin);
      },
      zamanAsimi: (metin: string) => {
        this.sunucuExcelDurum = "";
        this.$toast.warning(metin);
      },
    });
  }

  // DEV-3011 — detay kipi satırları açılır-kapanır: ana satır (ilk kolonu dolu)
  // + altındaki kopya satırlar (detaylar) gruplanır; kullanıcı isterse açar.
  acikGruplar: { [grup: number]: boolean } = {};

  get ilkKolonKodu(): string {
    return this.headers.length ? (this.headers[0] as any).value : "";
  }

  get detayliMi(): boolean {
    if (!this.isReady || !this.ilkKolonKodu) return false;
    return this.items.some((satir: any) => satir[this.ilkKolonKodu] === null
        || satir[this.ilkKolonKodu] === undefined);
  }

  // DEV-3224 (TDT-3492) — sıralama durumu. Detay kipinde sıralamayı Vuetify'a
  // bırakmıyoruz: düz listeyi sıralasa dosya satırı ile detay satırları
  // birbirinden kopardı (ölçüldü: satırların %63'ü dosya alanı boş). Bunun yerine
  // DOSYALAR sıralanıyor, her dosya detaylarıyla birlikte taşınıyor.
  siralamaKolonu: string[] = [];
  siralamaTers: boolean[] = [];

  /** Vuetify'ın kendi sıralamasını devre dışı bırakır; sıra gorunurSatirlar'da kurulur. */
  siralamayiDevralt(items: Array<object>): Array<object> {
    return items;
  }

  /** Türkçe sıralama; sayı ve gg/aa/yyyy tarihi metin gibi değil değerince sıralanır. */
  private static degerKarsilastir(a: any, b: any): number {
    const bosA = a === null || a === undefined || a === '';
    const bosB = b === null || b === undefined || b === '';
    if (bosA && bosB) return 0;
    if (bosA) return 1; // boşlar daima sona
    if (bosB) return -1;

    const tarih = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const ta = tarih.exec(String(a));
    const tb = tarih.exec(String(b));
    if (ta && tb) {
      return (ta[3] + ta[2] + ta[1]).localeCompare(tb[3] + tb[2] + tb[1]);
    }

    const na = typeof a === 'number' ? a : Number(String(a).replace(',', '.'));
    const nb = typeof b === 'number' ? b : Number(String(b).replace(',', '.'));
    if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;

    return String(a).localeCompare(String(b), 'tr');
  }

  get satirGruplari(): Array<{ ana: any; detaylar: any[]; kimlik: number }> {
    const gruplar: Array<{ ana: any; detaylar: any[]; kimlik: number }> = [];
    this.items.forEach((satir: any, sira: number) => {
      const anaMi = satir[this.ilkKolonKodu] !== null && satir[this.ilkKolonKodu] !== undefined;
      if (anaMi || gruplar.length === 0) {
        // kimlik = kaynak listedeki konum; sıralama değişse de açık/kapalı
        // durumu doğru gruba bağlı kalsın diye indeks yerine bu kullanılıyor.
        gruplar.push({ana: satir, detaylar: [], kimlik: sira});
      } else {
        gruplar[gruplar.length - 1].detaylar.push(satir);
      }
    });
    return gruplar;
  }

  /** Dosyalar seçili kolona göre sıralı; detaylar dosyasıyla birlikte taşınır. */
  private siraliGruplar(): Array<{ ana: any; detaylar: any[]; kimlik: number }> {
    const gruplar = [...this.satirGruplari];
    const kolon = this.siralamaKolonu[0];
    if (!kolon) {
      return gruplar;
    }
    const ters = this.siralamaTers[0] === true;
    gruplar.sort((a, b) => {
      const fark = RaporDetayView.degerKarsilastir(a.ana[kolon], b.ana[kolon]);
      return ters ? -fark : fark;
    });
    return gruplar;
  }

  get gorunurSatirlar(): Array<object> {
    if (!this.detayliMi || this.printMode) return this.items;

    const sonuc: Array<object> = [];
    this.siraliGruplar().forEach((grup) => {
      sonuc.push({...grup.ana, __grup: grup.kimlik, __detaySayisi: grup.detaylar.length});
      if (this.acikGruplar[grup.kimlik]) {
        grup.detaylar.forEach(d => sonuc.push(d));
      }
    });
    return sonuc;
  }

  /**
   * DEV-3224 (TDT-3492) — Excel, EKRANDAKİ sırayla iner; kapalı grupların
   * detayları da yazılır. Böylece kullanıcı sıralamayı ekranda yapıp sıralı
   * dosyayı indiriyor ve Excel'de sonradan sıralamak zorunda kalmıyor — asıl
   * şikayet, Excel'de sıralayınca dosya alanı boş satırların dosyalarından
   * kopmasıydı.
   */
  get excelSatirlari(): Array<Record<string, unknown>> {
    if (!this.detayliMi) {
      return this.items as Array<Record<string, unknown>>;
    }
    const sonuc: Array<Record<string, unknown>> = [];
    this.siraliGruplar().forEach((grup) => {
      sonuc.push(grup.ana);
      grup.detaylar.forEach(d => sonuc.push(d));
    });
    return sonuc;
  }

  get tabloBasliklari(): Array<object> {
    if (!this.detayliMi) return this.headers;
    return [{text: "Detay", value: "__detay", sortable: false, width: 78, align: "center"}, ...this.headers];
  }

  grupToggle(grup: number) {
    this.$set(this.acikGruplar, grup, !this.acikGruplar[grup]);
  }

  // DEV-3011 — grafik (bar/pie): raporda MEVCUT ilk kategorik alanın dağılımı.
  // Detay kipinin kopya satırlarında kategorik alanlar boş olduğundan sayım
  // kendiliğinden dosya bazlı kalır.
  private static readonly KATEGORIK_KODLAR: Array<{ kod: string; baslik: string }> = [
    {kod: "takip_durumu", baslik: "Dosya Durumu"},
    {kod: "takip_tipi_aciklama", baslik: "Takip Tipi"},
    {kod: "takip_yolu_isim", baslik: "Takip Yolu"},
    {kod: "tahsilat_turu", baslik: "Tahsilat Türü"},
    {kod: "masraf_cesidi", baslik: "Masraf Çeşidi"},
    {kod: "icra_dairesi", baslik: "İcra Dairesi"},
    {kod: "dosya_sorumlusu", baslik: "Dosya Sorumlusu"},
  ];

  /** Grafiğin gruplandığı kategorik alan; rapora böyle bir alan girmemişse grafik yok. */
  get grafikAlani(): { kod: string; baslik: string } | null {
    if (this.grafikTercihi !== "bar" && this.grafikTercihi !== "pie") return null;
    if (!this.isReady || this.items.length === 0) return null;
    const kodlar = this.headers.map((h: any) => h.value);
    return RaporDetayView.KATEGORIK_KODLAR.find(k => kodlar.indexOf(k.kod) > -1) || null;
  }

  /**
   * Dağılım büyükten küçüğe sıralanır; ilk 8 kategori gösterilir, kalanlar
   * "Diğer" altında toplanır. (Ölçüldü: icra dairesi gibi alanlarda 40+ kategori
   * çıkabiliyor ve grafik okunamaz hale geliyordu.)
   */
  private static readonly GRAFIK_KATEGORI_SINIRI = 8;

  get grafikSerisi(): { etiketler: string[]; degerler: number[]; toplam: number } | null {
    const aday = this.grafikAlani;
    if (!aday) return null;
    const sayim: { [etiket: string]: number } = {};
    let toplam = 0;
    this.items.forEach((satir: any) => {
      const deger = satir[aday.kod];
      if (deger === null || deger === undefined || deger === "") return;
      const etiket = String(deger).split("\n")[0];
      sayim[etiket] = (sayim[etiket] || 0) + 1;
      toplam++;
    });
    let etiketler = Object.keys(sayim).sort((a, b) => sayim[b] - sayim[a]);
    if (etiketler.length === 0) return null;
    let degerler = etiketler.map(e => sayim[e]);
    const sinir = RaporDetayView.GRAFIK_KATEGORI_SINIRI;
    if (etiketler.length > sinir) {
      const kalan = degerler.slice(sinir).reduce((t, v) => t + v, 0);
      etiketler = etiketler.slice(0, sinir).concat(["Diğer (" + (Object.keys(sayim).length - sinir) + ")"]);
      degerler = degerler.slice(0, sinir).concat([kalan]);
    }
    return {etiketler, degerler, toplam};
  }

  get grafikBaslik(): string {
    const aday = this.grafikAlani;
    return aday ? aday.baslik + " Dağılımı" : "";
  }

  get grafikAltBaslik(): string {
    const seri = this.grafikSerisi;
    if (!seri) return "";
    // Detay kipinde kopya satırlarda kategorik alan boş kalır; grafik yalnız
    // değeri olan satırları sayar. Üstteki "N kayıt" ile karışmasın diye açıkça yazılır.
    return "değeri olan " + seri.toplam + " kayıt · " + seri.etiketler.length + " kategori";
  }

  /** Grafik renkleri temadan gelir; ürün teması değişince grafik de değişir. */
  get grafikRenkleri(): string[] {
    const stil = getComputedStyle(document.documentElement);
    const oku = (ad: string, varsayilan: string) => (stil.getPropertyValue(ad) || "").trim() || varsayilan;
    return [
      oku("--primary", "#9d7ae8"),
      oku("--chart-2", "#33c481"),
      oku("--chart-3", "#ffa826"),
      oku("--chart-4", "#f45150"),
      oku("--chart-5", "#00bbd4"),
      oku("--primary-lighten-2", "#c0a8f0"),
      oku("--primary-darken-2", "#6a3fc0"),
      "#98a2b3",
      "#cbd5e1",
    ];
  }

  /** Grafiğin altındaki okunur döküm: etiket · adet · yüzde */
  get grafikDagilim(): Array<{ etiket: string; deger: number; yuzde: string; renk: string }> {
    const seri = this.grafikSerisi;
    if (!seri) return [];
    const renkler = this.grafikRenkleri;
    const toplam = seri.degerler.reduce((t, v) => t + v, 0) || 1;
    return seri.etiketler.map((etiket, i) => ({
      etiket,
      deger: seri.degerler[i],
      yuzde: "%" + Math.round((seri.degerler[i] / toplam) * 100),
      renk: renkler[i % renkler.length],
    }));
  }

  /** Yatay çubukta kategori sayısı arttıkça yükseklik büyür, çubuklar ezilmez. */
  get grafikYuksekligi(): number {
    if (this.grafikTercihi === "pie") return 280;
    const seri = this.grafikSerisi;
    const adet = seri ? seri.etiketler.length : 1;
    return Math.max(180, Math.min(420, 60 + adet * 34));
  }

  get grafikAyarlari(): object {
    const seri = this.grafikSerisi;
    const etiketler = seri ? seri.etiketler : [];
    const renkler = this.grafikRenkleri;
    if (this.grafikTercihi === "pie") {
      return {
        chart: {toolbar: {show: false}, fontFamily: "inherit"},
        labels: etiketler,
        colors: renkler,
        legend: {show: false},
        stroke: {width: 2, colors: ["#fff"]},
        dataLabels: {enabled: true, formatter: (v: number) => Math.round(v) + "%"},
        plotOptions: {pie: {donut: {size: "62%"}}},
        tooltip: {y: {formatter: (v: number) => v + " kayıt"}},
      };
    }
    // Kategori adları uzun olabildiği için yatay çubuk: etiketler kırpılmadan okunur
    return {
      chart: {toolbar: {show: false}, fontFamily: "inherit"},
      colors: renkler,
      plotOptions: {bar: {borderRadius: 4, horizontal: true, barHeight: "62%", distributed: true}},
      xaxis: {categories: etiketler, labels: {style: {fontSize: "12px"}}},
      yaxis: {labels: {style: {fontSize: "12px"}, maxWidth: 220}},
      grid: {borderColor: "rgba(0,0,0,.06)", xaxis: {lines: {show: true}}, yaxis: {lines: {show: false}}},
      legend: {show: false},
      dataLabels: {enabled: true, style: {fontSize: "12px", fontWeight: 700, colors: ["#fff"]}},
      tooltip: {y: {formatter: (v: number) => v + " kayıt"}},
    };
  }

  // DEV-3011 — sonuç üstünde özet kartları. Yalnız rapora eklenmiş parasal
  // toplam kolonlarından üretilir; kolon yoksa kart da yok.
  private static readonly OZET_KOLONLARI: Array<{ kod: string; baslik: string }> = [
    {kod: "takip_toplami", baslik: "Toplam Takip"},
    {kod: "genel_toplam", baslik: "Genel Toplam"},
    {kod: "tahsilat_toplami", baslik: "Tahsilat (Net)"},
    {kod: "tahsilat_toplami_brut", baslik: "Tahsilat (Brüt)"},
    {kod: "kalan_tutar", baslik: "Kalan Bakiye"},
    {kod: "masraf_toplami", baslik: "Toplam Masraf"},
  ];

  get ozetKartlari(): Array<{ baslik: string; deger: string; vurgulu: boolean; renk: string }> {
    if (!this.isReady || !this.items || this.items.length === 0) return [];
    const mevcutKodlar = this.headers.map((h: any) => h.value);
    const kartlar: Array<{ baslik: string; deger: string; vurgulu: boolean; renk: string }> = [];
    const toplamlar: { [kod: string]: number } = {};

    RaporDetayView.OZET_KOLONLARI.forEach((tanim) => {
      if (mevcutKodlar.indexOf(tanim.kod) === -1) return;
      let toplam = 0;
      let cokluParaBirimi = false;
      this.items.forEach((satir: any) => {
        const ham = satir[tanim.kod];
        if (ham === null || ham === undefined || ham === "") return;
        // Tahsilat ve asıl alacak hücreleri para birimi başına satır taşır
        // ("1000\n250"); farklı para birimlerini toplamak yanlış sonuç verir.
        const parcalar = String(ham).split("\n").filter((p: string) => p !== "");
        if (parcalar.length > 1) {
          cokluParaBirimi = true;
          return;
        }
        const sayi = parseFloat(parcalar[0]);
        if (!isNaN(sayi)) toplam += sayi;
      });
      if (cokluParaBirimi) {
        kartlar.push({baslik: tanim.baslik, deger: "çoklu para birimi", vurgulu: false, renk: ""});
        return;
      }
      toplamlar[tanim.kod] = toplam;
      kartlar.push({baslik: tanim.baslik, deger: this.tutarStr(toplam), vurgulu: false, renk: RaporDetayView.kartRengi(tanim.kod)});
    });

    // Tahsilat oranı: iki toplam da raporda varsa hesaplanır
    const takip = toplamlar["takip_toplami"] ?? toplamlar["genel_toplam"];
    // Oran net tahsilat üzerinden; rapora yalnız brüt eklendiyse ondan hesaplanır
    const tahsilat = toplamlar["tahsilat_toplami"] ?? toplamlar["tahsilat_toplami_brut"];
    if (takip !== undefined && tahsilat !== undefined && takip > 0) {
      kartlar.push({
        baslik: "Tahsilat Oranı",
        deger: "%" + Math.round((tahsilat / takip) * 100),
        vurgulu: true,
        renk: "",
      });
    }
    // Vurgulu kart (tahsilat oranı) her zaman görünsün: dördüncü sıraya alınır
    const vurgulu = kartlar.filter((k) => k.vurgulu);
    const digerleri = kartlar.filter((k) => !k.vurgulu);
    return digerleri.slice(0, 4 - vurgulu.length).concat(vurgulu);
  }

  /** Tahsilat olumlu, kalan bakiye olumsuz okunur; diğerleri nötr. */
  private static kartRengi(kod: string): string {
    if (kod === "tahsilat_toplami" || kod === "tahsilat_toplami_brut") return "olumlu";
    if (kod === "kalan_tutar") return "olumsuz";
    return "";
  }

  private tutarStr(deger: number): string {
    return deger.toLocaleString("tr-TR", {minimumFractionDigits: 2, maximumFractionDigits: 2}) + " TL";
  }

  oncekiSayfa(){
    this.$router.push(this.programPaths.ofispro + '/raporlar')
  }
  mounted() {
    this.isReady = false;
    this.load();
  }

  load() {
    this.$http.get('/api/v1/rapor/' + this.$route.params.id).then((response) => {
      this.raporAdi = response.data.isim;
      this.grafikTercihi = response.data.grafik_tercihi || "kartlar";
      response.data.alanlar.forEach((raporAlani: IRaporAlani) => {
        this.headers.push({text: raporAlani.rapor_alan.isim, value: raporAlani.rapor_alan.kod});
        this.excelKolonlari.push({
          baslik: raporAlani.rapor_alan.isim,
          kod: raporAlani.rapor_alan.kod,
          sayisal: raporAlani.rapor_alan.kosul_input === 'tutar',
        });
      });
      this.$http.post('/api/v1/rapor/' + this.$route.params.id + '/render').then((response) => {
        this.items = response.data;
        this.isReady = true;
      })
    })
  }

  ciktiAl() {
    this.printMode = true;
    this.$nextTick(() => {
      this.$helper.printComponent('rapor');
      this.printMode = false;
    });
  }
}

interface IRaporAlani {
  gosterim_sirasi: number,
  id: number,
  rapor_alan: IRaporAlan,
  rapor_alan_id: number,
  rapor_id: number
}

interface IRaporAlan {
  id: number,
  isim: string,
  kod: string,
  field: string,
  joins: string,
  kosul_input: string,
  kosul_olabilir_mi: boolean,
  kosul_operatorleri: Array<any>
}
